import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { theme } from '@jadwalh/ui';

interface MentionItem {
  id: number;
  display: string;
}

interface PortalMentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  mentionItems: MentionItem[];
  style?: React.CSSProperties;
  className?: string;
}

const PortalMentionInput: React.FC<PortalMentionInputProps> = ({
  value,
  onChange,
  placeholder = '',
  mentionItems,
  style,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter mention items based on query
  const filteredItems = mentionItems.filter(item =>
    item.display.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  // Simplified position calculation - position at input center for overflow cases
  const calculatePosition = useCallback(() => {
    if (!inputRef.current) return { top: 0, left: 0 };

    const rect = inputRef.current.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(inputRef.current);
    const isRTL = computedStyle.direction === 'rtl' || computedStyle.textAlign === 'right';

    // Check if text is overflowing
    const isOverflowing = inputRef.current.scrollWidth > inputRef.current.clientWidth;

    // Dropdown dimensions
    const dropdownWidth = 160; // maxWidth from styles
    const dropdownMargin = 8; // margin from viewport edge

    let leftPosition;
    if (isOverflowing) {
      // If text is overflowing, position at the end of the text (right side for RTL)
      if (isRTL) {
        leftPosition = rect.left + 12; // Position at the start (which is the end for RTL)
      } else {
        leftPosition = rect.right - 12; // Position at the end (right side)
      }
    } else {
      // If not overflowing, try to position at cursor
      const cursorPosition = inputRef.current.selectionStart || 0;

      // Create a temporary element to measure text width
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.whiteSpace = 'pre';
      tempDiv.style.font = computedStyle.font;
      tempDiv.style.fontSize = computedStyle.fontSize;
      tempDiv.style.fontFamily = computedStyle.fontFamily;
      tempDiv.style.fontWeight = computedStyle.fontWeight;
      tempDiv.style.letterSpacing = computedStyle.letterSpacing;
      tempDiv.style.wordSpacing = computedStyle.wordSpacing;
      tempDiv.style.textTransform = computedStyle.textTransform;
      tempDiv.style.direction = computedStyle.direction;
      tempDiv.style.lineHeight = computedStyle.lineHeight;

      const textUpToCursor = value.substring(0, cursorPosition);
      tempDiv.textContent = textUpToCursor;

      document.body.appendChild(tempDiv);
      const textWidth = tempDiv.offsetWidth;
      document.body.removeChild(tempDiv);

      const inputPadding = parseInt(computedStyle.paddingLeft) || 12;
      const inputPaddingRight = parseInt(computedStyle.paddingRight) || 12;

      if (isRTL) {
        leftPosition = rect.right - inputPaddingRight - textWidth;
      } else {
        leftPosition = rect.left + inputPadding + textWidth;
      }
    }

    // Constrain to viewport bounds
    const viewportWidth = window.innerWidth;

    // Ensure dropdown doesn't go off the right edge
    if (leftPosition + dropdownWidth > viewportWidth - dropdownMargin) {
      leftPosition = viewportWidth - dropdownWidth - dropdownMargin;
    }

    // Ensure dropdown doesn't go off the left edge
    if (leftPosition < dropdownMargin) {
      leftPosition = dropdownMargin;
    }

    const position = {
      top: rect.bottom - 2,
      left: leftPosition,
    };

    return position;
  }, [value]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;
    
    // Find the last @ symbol before cursor
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Check if there's a space after @ (which would close the mention)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        // We're in a mention
        setMentionStart(lastAtIndex);
        setMentionQuery(textAfterAt);
        setPosition(calculatePosition());
        setIsOpen(true);
        setSelectedIndex(0);
      } else {
        setIsOpen(false);
        setMentionStart(-1);
        setMentionQuery('');
      }
    } else {
      setIsOpen(false);
      setMentionStart(-1);
      setMentionQuery('');
    }
    
    onChange(newValue);
  };

  // Handle mention selection
  const selectMention = useCallback((item: MentionItem) => {
    if (mentionStart === -1) return;
    
    const beforeMention = value.substring(0, mentionStart);
    const afterMention = value.substring(mentionStart + mentionQuery.length + 1);
    const newValue = beforeMention + item.display + ' ' + afterMention;
    
    onChange(newValue);
    setIsOpen(false);
    setMentionStart(-1);
    setMentionQuery('');
    setSelectedIndex(0);
    
    // Focus back to input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [value, mentionStart, mentionQuery, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredItems.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredItems.length) % filteredItems.length);
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        selectMention(filteredItems[selectedIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        setMentionStart(-1);
        setMentionQuery('');
        setSelectedIndex(0);
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        // Update position when cursor moves
        setTimeout(() => {
          setPosition(calculatePosition());
        }, 0);
        break;
    }
  };

  // Handle cursor movement - update position based on cursor
  const handleSelectionChange = () => {
    if (isOpen && inputRef.current) {
      // Force position update when cursor moves
      setTimeout(() => {
        setPosition(calculatePosition());
      }, 0);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setMentionStart(-1);
        setMentionQuery('');
        setSelectedIndex(0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position update on scroll/resize and periodic updates
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      setPosition(calculatePosition());
    };

    // Update position immediately
    updatePosition();

    // Add event listeners
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    
    // Add input scroll listener for when text overflows
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('scroll', updatePosition, { passive: true });
    }

    // Add periodic update to ensure it stays in sync
    const interval = setInterval(updatePosition, 50);

    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      if (inputElement) {
        inputElement.removeEventListener('scroll', updatePosition);
      }
    };
  }, [isOpen, calculatePosition]);

  // Render dropdown portal
  const renderDropdown = () => {
    if (!isOpen || filteredItems.length === 0) return null;
    
    // Debug logging
    // console.log('Rendering dropdown with position:', position);
    
    const dropdown = (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 10001,
          backgroundColor: '#ffffff',
          border: `1px solid ${theme.colours.newOutline}`,
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          padding: '6px',
          minWidth: '140px',
          maxWidth: '160px',
          maxHeight: '300px',
          overflowY: 'auto',
        }}
      >
        {filteredItems.map((item, index) => (
          <div
            key={item.id}
            style={{
              padding: '6px 10px',
              cursor: 'pointer',
              borderRadius: '4px',
              transition: 'background-color 0.15s ease',
              fontSize: '13px',
              lineHeight: '1.3',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: index === selectedIndex ? theme.colours.black2 : 'transparent',
              color: index === selectedIndex ? '#ffffff' : 'inherit',
            }}
            onMouseEnter={() => setSelectedIndex(index)}
            onClick={() => selectMention(item)}
          >
            {item.display}
          </div>
        ))}
      </div>
    );

    return createPortal(dropdown, document.body);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelectionChange}
        onMouseUp={handleSelectionChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: `1px solid ${theme.colours.newOutline}`,
          borderRadius: '6px',
          fontSize: '13px',
          outline: 'none',
          ...style,
        }}
        className={className}
      />
      {renderDropdown()}
    </>
  );
};

export default PortalMentionInput;